import { buildApiHeaders } from '@/lib/api-client'
import type { EnsureEnvelopeAccess } from '@/lib/envelope-access'
import { ENVELOPE_ACCESS_TOKEN_HEADER } from '@/lib/envelope-access'
import { logger } from '@/lib/logger'

const API_URL = import.meta.env.VITE_API_URL

export function addFileNameSuffix(fileName: string, suffix: string): string {
  const lastDot = fileName.lastIndexOf('.')
  if (lastDot === -1) return `${fileName} - ${suffix}`
  return `${fileName.slice(0, lastDot)} - ${suffix}${fileName.slice(lastDot)}`
}

function ensurePdfExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.')
  if (lastDot === -1) return `${fileName}.pdf`
  return `${fileName.slice(0, lastDot)}.pdf`
}

interface FetchBlobOptions {
  getAccessToken?: EnsureEnvelopeAccess
}

const requestBlob = (url: string, accessToken: string | null) =>
  fetch(url, {
    headers: accessToken
      ? { ...buildApiHeaders(), [ENVELOPE_ACCESS_TOKEN_HEADER]: accessToken }
      : buildApiHeaders(),
  })

/**
 * Fetches a blob from the API using native fetch instead of axios.
 *
 * This is intentional: when the API responds with a 302 redirect to an external
 * storage URL (e.g. R2/S3), the Fetch API drops the Authorization header on the
 * cross-origin redirect per the Fetch spec. Axios (XHR) forwards it, which
 * causes unnecessary CORS preflight failures on the storage URL.
 */
export async function fetchBlobFromApi(
  apiPath: string,
  { getAccessToken }: FetchBlobOptions = {},
): Promise<Blob> {
  const url = `${API_URL}${apiPath}`

  logger.debug('Blob download started', { url: apiPath }, 'documents')

  const accessToken = (await getAccessToken?.()) ?? null
  let response = await requestBlob(url, accessToken)
  if (response.status === 401 && accessToken && getAccessToken) {
    response = await requestBlob(
      url,
      await getAccessToken({ rejected: accessToken }),
    )
  }

  if (!response.ok) {
    logger.error(
      'Blob download failed',
      {
        url: apiPath,
        status: response.status,
        statusText: response.statusText,
      },
      'documents',
    )
    throw new Error(
      `Failed to fetch blob: ${response.status} ${response.statusText}`,
    )
  }

  const blob = await response.blob()

  logger.info(
    'Blob download completed',
    { url: apiPath, size: blob.size },
    'documents',
  )

  return blob
}

export async function downloadBlob(
  apiPath: string,
  fileName: string,
  options: FetchBlobOptions = {},
) {
  const blob = await fetchBlobFromApi(apiPath, options)
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = ensurePdfExtension(fileName)
  a.click()
  URL.revokeObjectURL(url)
}

export async function openBlob(apiPath: string) {
  const blob = await fetchBlobFromApi(apiPath)
  const blobUrl = URL.createObjectURL(blob)
  window.open(blobUrl, '_blank')
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
}

export async function downloadSignedDocument(
  documentId: string,
  fileName: string,
  getAccessToken?: EnsureEnvelopeAccess,
) {
  await downloadBlob(`/v1/documents/${documentId}/signed`, fileName, {
    getAccessToken,
  })
}

export async function downloadOriginalDocument(
  documentId: string,
  fileName: string,
  getAccessToken?: EnsureEnvelopeAccess,
) {
  await downloadBlob(`/v1/documents/${documentId}/original`, fileName, {
    getAccessToken,
  })
}

export async function downloadCertificate(
  envelopeId: string,
  fileName: string,
  getAccessToken?: EnsureEnvelopeAccess,
) {
  await downloadBlob(`/v1/envelopes/${envelopeId}/certificate`, fileName, {
    getAccessToken,
  })
}

/**
 * Downloads a signer's attachment uploaded to a SignMark of type Attachment.
 * Skips the PDF-extension forcing in `downloadBlob` since attachments preserve
 * the user-uploaded filename + extension verbatim.
 */
export async function downloadSignMarkAttachment(
  envelopeId: string,
  signMarkId: string,
  attachmentId: string,
  fileName: string,
) {
  const blob = await fetchBlobFromApi(
    `/v1/ecm/envelopes/${envelopeId}/sign-marks/${signMarkId}/attachments/${attachmentId}/download`,
  )
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.click()
  URL.revokeObjectURL(url)
}
